import { CORE_EVENTS, PURCHASELY_API_KEY } from "@core/constants";
import { Logger } from "@core/helpers/logger.helpers";
import { isIOS } from "@core/plugins/mobile";
import { EventManager } from "@core/managers/event.manager";
import { EtabProfileService } from "@core/services";
import { AuthenticationManager } from "@core/managers/authentication.manager";

const noop = () => {};

export class PurchaselyManager {
  static get Purchasely() {
    return window.Purchasely;
  }

  static puchaselyPluginInstalled() {
    return PurchaselyManager.Purchasely !== undefined;
  }

  static isIAPActivated() {
    // RG: for the moment we activate IAP only on IOS
    return PurchaselyManager.puchaselyPluginInstalled() && isIOS;
  }

  /**
   * https://docs.purchasely.com/quick-start/sdk-implementation#configuration
   * start purchasely Running mode
   */
  static async start({ router }) {
    try {
      PurchaselyManager.Purchasely.start(
        //
        `${PURCHASELY_API_KEY}`,
        ["Google"],
        false,
        null,
        PurchaselyManager.Purchasely.LogLevel.DEBUG,
        PurchaselyManager.Purchasely.RunningMode.full,
        (isConfigured) => {
          Logger.log("PurchaselyManager.isConfigured - Purchasely configured", isConfigured);
        },
        (error) => {
          Logger.error("PurchaselyManager.start - Purchasely failed to start", error);
        },
      );

      await PurchaselyManager.handlePurchaselyUserIdentification();
      PurchaselyManager.registerEvents({ router });
      Logger.log("PurchaselyManager.start - Purchasely started successfully");
    } catch (error) {
      Logger.error("PurchaselyManager.start - Purchasely failed to start", error);
    }
  }

  static async handlePurchaselyUserIdentification() {
    try {
      const userIsAuthenticated = await AuthenticationManager.isUserAuthenticated();
      if (userIsAuthenticated) {
        Logger.log(`PurchaselyManager.handlePurchaselyUserIdentification - User already authenticated, let's log them`);
        await PurchaselyManager.userLogin();
      } else {
        Logger.log(`PurchaselyManager.handlePurchaselyUserIdentification - User not authenticated, let's wait for login`);
      }
      return null;
    } catch (error) {
      Logger.error("PurchaselyManager.handlePurchaselyUserIdentification - failed", error);
      return null;
    }
  }

  /**
   * https://docs.purchasely.com/quick-start/sdk-implementation#unlock-content
   * show purchasely's Paywall
   */
  static showPayWall({ placementId, contendId = null, fullscreen = true, successCallback = noop, errorCallback = noop } = {}) {
    try {
      PurchaselyManager.Purchasely.presentPresentationForPlacement(
        //
        placementId,
        contendId,
        fullscreen,
        successCallback,
        errorCallback,
      );
      Logger.log(`PurchaselyManager.showPayWall OK - placementId:${placementId}, contendId${contendId}, fullscreen${fullscreen}`);
    } catch (error) {
      Logger.error(`PurchaselyManager.showPayWall NOK - placementId:${placementId}, contendId${contendId}, fullscreen${fullscreen}`, error);
    }
  }

  /**
   * https://docs.purchasely.com/quick-start-1/sdk-configuration/config-appendices/set-user-id
   * allow to set user id and send it to purchasely
   */
  static async userLogin() {
    let etabId;
    try {
      etabId = await EtabProfileService.getEtabId();
      if (etabId) {
        PurchaselyManager.Purchasely.userLogin(etabId.toString());
        Logger.log(`PurchaselyManager.userLogin OK - ${etabId}`);
      } else {
        throw new Error(`PurchaselyManager.userLogin NOK - no EtabId`);
      }
      return null;
    } catch (error) {
      Logger.error(`PurchaselyManager.userLogin NOK - ${etabId}`, error);
      return null;
    }
  }

  static userLogout() {
    try {
      PurchaselyManager.Purchasely.userLogout();
      Logger.log(`PurchaselyManager.userLogout OK`);
    } catch (error) {
      Logger.error(`PurchaselyManager.userLogout NOK`, error);
    }
  }

  static eventsHandler({ router }) {
    return async (event) => {
      Logger.log("PurchaselyManager.eventsHandlers", event);
    };
  }

  static registerEvents({ router }) {
    EventManager.on(CORE_EVENTS.ON_LOGIN, PurchaselyManager.userLogin);
    EventManager.on(CORE_EVENTS.ON_LOGOUT, PurchaselyManager.userLogout);
    PurchaselyManager.Purchasely.addEventsListener(PurchaselyManager.eventsHandler({ router }));
    Logger.info("## PurchaselyManager ### Successfully registered event");
  }
}
